import * as alertsAPI from '../api/alerts.api';
import { DATA_SOURCES, isCovered } from '@/config/constants';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { apiDataSource } from 'utils/entities';
import { useMemo } from 'react';

// Alert Config hooks
export const useAlertConfigs = () => {
    return useQuery('alertConfigs', alertsAPI.fetchAlertConfigs, {
        staleTime: 60000, // 1 minute
    });
};

export const useCreateAlertConfig = () => {
    const queryClient = useQueryClient();

    return useMutation(alertsAPI.createAlertConfig, {
        onSuccess: () => {
            queryClient.invalidateQueries('alertConfigs');
        },
    });
};

export const useUpdateAlertConfig = () => {
    const queryClient = useQueryClient();

    return useMutation(
        ({ alertConfigId, data }) =>
            alertsAPI.updateAlertConfig(alertConfigId, data),
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries('alertConfigs');
                queryClient.invalidateQueries([
                    'alertConfig',
                    variables.alertConfigId,
                ]);
            },
        },
    );
};

export const useDeleteAlertConfig = () => {
    const queryClient = useQueryClient();

    return useMutation(alertsAPI.deleteAlertConfig, {
        onSuccess: () => {
            queryClient.invalidateQueries('alertConfigs');
        },
    });
};

// Alert Preview & Task hooks
export const useGenerateAlertPreview = () => {
    return useMutation(alertsAPI.generateAlertPreview);
};

export const useTaskStatus = (taskId, options = {}) => {
    return useQuery(
        ['taskStatus', taskId],
        () => alertsAPI.fetchTaskStatus(taskId),
        {
            enabled: !!taskId,
            refetchInterval: (data) => {
                // Poll every 2 seconds if task is still running
                if (data?.status === 'creating' || data?.status === 'running') {
                    return 2000;
                }
                return false;
            },
            ...options,
        },
    );
};

export const useAlertPreview = (taskId, options = {}) => {
    return useQuery(
        ['alertPreview', taskId],
        () => alertsAPI.fetchAlertPreview(taskId),
        {
            enabled: !!taskId,
            staleTime: 300000, // 5 minutes
            ...options,
        },
    );
};

// Alert Sending hooks
export const useSendTestAlert = () => {
    return useMutation(alertsAPI.sendTestAlert);
};

// Generated Alerts hooks
export const useGeneratedAlerts = (limit = 50) => {
    return useQuery(
        ['generatedAlerts', limit],
        () => alertsAPI.fetchGeneratedAlerts(limit),
        {
            staleTime: 60000,
        },
    );
};

// Alert Contexts hooks
export const useAlertContexts = () => {
    return useQuery('alertContexts', alertsAPI.fetchAlertContexts, {
        staleTime: 60000,
        refetchInterval: (data) => {
            const contexts = data?.contexts || [];
            const hasPending = contexts.some(
                (ctx) =>
                    ctx.territory_status && ctx.territory_status !== 'ready',
            );
            return hasPending ? 5000 : false;
        },
    });
};

export const useCreateAlertContext = () => {
    const queryClient = useQueryClient();
    return useMutation(alertsAPI.createAlertContext, {
        onSuccess: () => {
            queryClient.invalidateQueries('alertContexts');
        },
    });
};

export const useUpdateAlertContext = () => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ alertContextId, data }) =>
            alertsAPI.updateAlertContext(alertContextId, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('alertContexts');
            },
        },
    );
};

export const useDeleteAlertContext = () => {
    const queryClient = useQueryClient();
    return useMutation(alertsAPI.deleteAlertContext, {
        onSuccess: () => {
            queryClient.invalidateQueries('alertContexts');
        },
    });
};

export const useGenerateTopicStarter = () => {
    return useMutation(alertsAPI.generateTopicStarter);
};

// Categories hooks
export const useAlertCategories = (dataSource) => {
    const source = apiDataSource(dataSource);
    return useQuery(
        ['alertCategories', source],
        () => alertsAPI.fetchAlertCategories(source),
        {
            enabled: !!dataSource,
            staleTime: 600000, // 10 minutes - categories don't change often
        },
    );
};

export const useAlertCategoryCoverage = ({
    country,
    selectedCategories = [],
    dataSource = DATA_SOURCES.TIKTOK,
} = {}) => {
    const {
        data: categoriesData,
        isLoading,
        error,
    } = useAlertCategories(dataSource);
    const allCategories = categoriesData?.categories || [];
    const coveredCategories = useMemo(() => {
        if (!country) return allCategories;
        return allCategories.filter((category) =>
            isCovered({
                geo: country,
                dataSource,
                cat1: category.cat1_label,
            }),
        );
    }, [allCategories, country, dataSource]);
    const categoriesById = useMemo(() => {
        return new Map(
            allCategories.map((category) => [category.id, category]),
        );
    }, [allCategories]);
    const cat2IdToCat1 = useMemo(() => {
        const map = new Map();
        allCategories.forEach((category) => {
            if (category.parent_id && !map.has(category.parent_id)) {
                map.set(category.parent_id, category.cat1_label);
            }
        });
        return map;
    }, [allCategories]);
    const unsupportedSelections = useMemo(() => {
        if (!selectedCategories?.length) return [];
        return selectedCategories
            .map((selected) => {
                if (selected.cat2) {
                    const cat1 = cat2IdToCat1.get(selected.id);
                    if (
                        !cat1 ||
                        !isCovered({
                            geo: country,
                            dataSource,
                            cat1,
                        })
                    ) {
                        return selected.cat2;
                    }
                    return null;
                }

                const category = categoriesById.get(selected.id);
                if (
                    !category ||
                    !isCovered({
                        geo: country,
                        dataSource,
                        cat1: category.cat1_label,
                    })
                ) {
                    return (
                        category?.name ||
                        selected.cat3 ||
                        selected.cat2 ||
                        'Unknown category'
                    );
                }
                return null;
            })
            .filter(Boolean);
    }, [selectedCategories, country, dataSource, categoriesById, cat2IdToCat1]);

    return {
        categories: coveredCategories,
        allCategories,
        isLoading,
        error,
        unsupportedSelections,
        hasUnsupportedSelections: unsupportedSelections.length > 0,
    };
};
